import React, { useRef, useEffect } from 'react';

import { BillingInfo } from 'components/BillingInfo/types';
// Type for ActivateCard form values
export type ActivateCardFormValues = {
  customId: string;
  billing?: BillingInfo;
};

import RootLayout from 'common/layout/root-layout';

import { Form, Segment, Button, Grid, Message, Header, Icon } from 'semantic';

import { useTranslation } from 'react-i18next';

import { submitBillingInfo } from 'components/BillingInfo/api';

import {
  PaymentMethodsWidgetProvider,
  useEnabledPaymentMethods,
} from 'contexts/enabledPaymentMethods';
import { BillingInfoFormikForm } from 'components/BillingInfo/formik-form';

import { useBillingInfoFormikValidationSchema } from 'components/BillingInfo/formik-validation';

import './activate.less';

import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import {
  PaymentMethodsWidgetCallbacks,
  tryStoringPaymentInstrument,
} from 'components/PaymentMethodsWidget/PaymentMethodsWidget';

import { AccountProvider, useAccount } from 'contexts/account';
import { Stack } from 'components/index';
import { useUser } from 'contexts/user';
import { isAccountAdmin } from 'utils/roles';
import InputField from 'components/form-fields/formik/InputField';
import { useFeatures } from 'contexts/features';
import { useActivateCardErrorFormatter } from './errors';

import { request } from 'utils/api';

const ACTIVATE_CARD_STORAGE_KEY = 'activateCard';

const customId = window.localStorage.getItem(ACTIVATE_CARD_STORAGE_KEY);

function ActivateCard() {
  const { t } = useTranslation();
  const { account, isBillingUpdateRequired, getAccount } = useAccount();
  const { hasFeature } = useFeatures();
  const isBicCollectionEnabled = hasFeature('billing_bic_collection');
  const errorFormatter = useActivateCardErrorFormatter();
  const [isLoading, setIsLoading] = React.useState(true);

  const [success, setSuccess] = React.useState(false);

  const { isPayBridgeEnabled, enabledPaymentMethods } =
    useEnabledPaymentMethods();

  const formikRef = useRef<FormikProps<any>>(null);

  const [error, setError] = React.useState<Error | null>(null);

  const paymentMethodsRef = useRef<PaymentMethodsWidgetCallbacks>(null);

  // we cannot access FF when the user is not logged in so enableBicCollection is passed always as true
  const billingInfoValidationSchema = useBillingInfoFormikValidationSchema(
    account?.type || 'individual',
    isBicCollectionEnabled
  );

  useEffect(() => {
    if (account && !isPayBridgeEnabled && !customId) {
      setIsLoading(false);
      return;
    }

    if (!account) {
      return;
    }

    tryStoringPaymentInstrument(account, enabledPaymentMethods)
      .then(
        // Only store the invite if the payment method is stored
        async (isStored) => {
          if (isStored && isPayBridgeEnabled) {
            if (getAccount) {
              await getAccount();
            }

            await request({
              method: 'POST',
              path: '/1/cards/activate',
              body: { customId },
            });
            localStorage.removeItem(ACTIVATE_CARD_STORAGE_KEY);
            setSuccess(true);
          }
        }
      )
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 4000);
      });
  }, [account, enabledPaymentMethods, customId, isPayBridgeEnabled]);

  const { user } = useUser();

  if (!user || !account) {
    return null;
  }

  const showBillingForm = isBillingUpdateRequired && isAccountAdmin(user);

  async function handleSubmit(values: ActivateCardFormValues) {
    if (!account) {
      return null;
    }

    if (showBillingForm && values.billing) {
      try {
        await submitBillingInfo(account, values.billing);
        if (isPayBridgeEnabled) {
          const response =
            await paymentMethodsRef.current?.authorizeActiveOption();

          if (response?.redirectUrl) {
            window.localStorage.setItem(
              ACTIVATE_CARD_STORAGE_KEY,
              values.customId
            );
            window.location.href = response.redirectUrl;
          }
          // continue the payment process does no need redirect
        }
      } catch (e: any) {
        if (e.details) {
          const unhandled = e.details?.filter(
            (error: any) => !errorFormatter(error, formikRef?.current)
          );
          if (unhandled.length) {
            setError(unhandled[0]);
          }
        } else {
          setError(e);
        }
      }
      if (isPayBridgeEnabled) {
        // Stop here as we need to redirect to payment provider
        return;
      }
    }

    // billing info is all solved so now we can activate the card

    try {
      await request({
        method: 'POST',
        path: '/1/cards/activate',
        body: { customId: values.customId },
      });
      setSuccess(true);
    } catch (e: any) {
      formikRef?.current?.setFieldError('customId', e.message);
    }
  }

  const schema = showBillingForm
    ? Yup.object({
        customId: Yup.string().required(
          t(
            'activate.validation.customIdRequired',
            'Card identifier is required'
          )
        ),

        billing: Yup.object(billingInfoValidationSchema),
      })
    : Yup.object({
        customId: Yup.string().required(
          t(
            'activate.validation.customIdRequired',
            'Card identifier is required'
          )
        ),
      });

  if (isLoading) {
    return (
      <Grid centered>
        <Grid.Column mobile={16} tablet={12} computer={12}>
          <Segment
            compact
            loading={true}
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '5em',
              paddingBottom: '5em',
            }}>
            {t('common.loading', 'Loading')}
          </Segment>
        </Grid.Column>
      </Grid>
    );
  }

  if (success) {
    return (
      <Grid centered>
        <Grid.Column mobile={16} tablet={12} computer={12}>
          <Segment
            compact
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '5em',
              paddingBottom: '5em',
            }}>
            <Header icon>
              <Icon name="circle-check" />
              {t('activate.success', 'Your card is now activated! Enjoy!')}
            </Header>
          </Segment>
        </Grid.Column>
      </Grid>
    );
  }

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={
        {
          customId: customId || '',
          billing: account.billing || {},
        } as ActivateCardFormValues
      }
      onSubmit={handleSubmit}
      validationSchema={schema}>
      {({ isSubmitting, handleSubmit }) => {
        return (
          <Form>
            <Grid centered>
              <Grid.Column mobile={16} tablet={12} computer={12}>
                <Segment
                  compact
                  loading={isLoading}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '3em',
                  }}>
                  <Stack noWrapper spacing={20} align="center">
                    <h3
                      style={{
                        margin: 0,
                      }}>
                      {t('activate.title', 'Activate Card')}
                    </h3>

                    <p style={{ maxWidth: '600px', textAlign: 'center' }}>
                      {t(
                        'activate.subTitle',
                        'To verify that your card has been properly delivered, please enter the identifier on the front of your card here'
                      )}
                    </p>

                    <Segment
                      padded
                      style={{
                        background: '#FCFCFC',
                        maxWidth: '400px',
                        borderRadius: '5px',
                        margin: 0,
                      }}>
                      <Form.Field required>
                        <label>
                          {t('activate.labelCardId', 'Card Identifier')}
                        </label>
                        <div
                          className="activate-input"
                          style={{
                            paddingTop: '10px',
                            marginBottom: '25px',
                          }}>
                          <InputField
                            fluid
                            autoFocus
                            placeholder={t(
                              'activate.placeholderHelpText',
                              'Example: NL-EFL-000000000-0 or C12345678'
                            )}
                            name="customId"
                            type="text"
                            required
                          />
                        </div>
                      </Form.Field>

                      <label
                        style={{
                          fontStyle: 'italic',
                          fontSize: '10pt',
                          color: '#2A2A2A',
                        }}>
                        {t(
                          'activate.cardNote',
                          'In case you want to add multiple users to have access to your charge station you can add them later in'
                        )}
                      </label>
                    </Segment>

                    {isBillingUpdateRequired && !showBillingForm && (
                      <Message error style={{ maxWidth: '400px' }}>
                        {t(
                          'activate.noBillingInfo',
                          'This card can not be activated because its missing billing information. Please contact your account administrator to add billing information to your account.'
                        )}
                      </Message>
                    )}

                    {showBillingForm && (
                      <Stack
                        style={{
                          width: '100%',
                        }}
                        noWrapper
                        spacing={20}
                        align="center">
                        <h3>
                          {t('activate.billingDetails', 'Billing Details')}
                        </h3>
                        <Segment
                          padded
                          style={{
                            background: '#FCFCFC',
                            borderRadius: '5px',
                            width: '100%',
                          }}>
                          <PaymentMethodsWidgetProvider ref={paymentMethodsRef}>
                            <BillingInfoFormikForm
                              accountType={account.type}
                              enableBicCollection={isBicCollectionEnabled}
                              enableCountryCodeInput={
                                !account.billing?.countryCode
                              }
                              objectPath="billing"
                              showErrorLabels={false}
                              redirectUrl={window.location.href}
                            />
                          </PaymentMethodsWidgetProvider>
                        </Segment>
                      </Stack>
                    )}

                    {error && (
                      <Message error>
                        <p>{t('activate.error', 'An error occurred')}</p>
                        <p>{error.message}</p>
                      </Message>
                    )}

                    <Button
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      primary
                      content={t('activate.buttonActivate', 'Activate Card')}
                      onClick={handleSubmit}
                      style={{ width: '200px', fontWeight: 'normal' }}
                    />
                  </Stack>
                </Segment>
              </Grid.Column>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}

export default function ActivateCardWrapper() {
  return (
    <RootLayout>
      <AccountProvider>
        <ActivateCard />
      </AccountProvider>
    </RootLayout>
  );
}
